import type { AppTranslateFn } from "@/hooks/use-app-translation";
import type { AssetType, AssetTypeDisplay } from "@/services/asset/asset.types";
import {
	type ConfigurationTypeTableAccessDisplay,
	type ConfigurationTypeTableAccessKeys,
	getConfigurationTypeTableAccessDisplay,
} from "@/utils/configuration-type-table-access/configuration-type-table-access.utils";

export type AssetTypeScope = "platform" | "organization";

export type AssetTypeDrawerMode = "view" | "edit";

/** Org-system types must stay in view mode even if URL search has `mode=edit`. */
export function getEffectiveAssetTypeDrawerMode(
	canEdit: boolean,
	requestedMode: AssetTypeDrawerMode,
): AssetTypeDrawerMode {
	return canEdit && requestedMode === "edit" ? "edit" : "view";
}

export function normalizeAssetTypeSource(row: AssetTypeDisplay): string {
	if (typeof row.defaultType === "boolean") {
		return row.defaultType ? "system" : "custom";
	}
	return (row.source ?? "").trim().toLowerCase();
}

export function isSystemAssetType(row: AssetTypeDisplay): boolean {
	if (typeof row.defaultType === "boolean") return row.defaultType;
	const source = normalizeAssetTypeSource(row);
	return source === "" || source === "system";
}

export function isCustomAssetType(row: AssetTypeDisplay): boolean {
	if (typeof row.defaultType === "boolean") return !row.defaultType;
	return normalizeAssetTypeSource(row) === "custom";
}

/**
 * Whether the delete action should be shown in the given scope.
 * Platform scope: system types may be deleted.
 * Organization scope: only custom types may be deleted.
 */
export function canShowDeleteAssetType(
	scope: AssetTypeScope,
	row: AssetTypeDisplay,
): boolean {
	if (scope === "platform") return isSystemAssetType(row);
	return isCustomAssetType(row);
}

export function canShowAccessTab(scope: AssetTypeScope): boolean {
	return scope === "organization";
}

export function canShowActivityTab(
	scope: AssetTypeScope,
	_row: AssetTypeDisplay,
): boolean {
	if (scope === "platform" || scope === "organization") return true;
	return false;
}

export function getAssetTypeDisplayName(row: AssetTypeDisplay): string {
	return row.name?.trim() || "";
}

/** Matches Access tab + detail draft: org-wide disabled when `enabled === false`. */
export function isAssetTypeOrgAccessDisabled(
	row: Pick<AssetType | AssetTypeDisplay, "enabled">,
): boolean {
	return row.enabled === false;
}

/**
 * Org-admin configuration table: all types remain visible when org-disabled
 * so Access tab changes are reflected in the table (e.g. "Not available").
 */
export function shouldShowOrgAdminAssetTypesTableRow(
	_row: Pick<
		AssetType | AssetTypeDisplay,
		"defaultType" | "enabled" | "source"
	>,
): boolean {
	return true;
}

export type AssetTypeTableAccessDisplay = ConfigurationTypeTableAccessDisplay;

const ASSET_TYPE_TABLE_ACCESS_KEYS = {
	available: "admin.assetTypes.table.access.available",
	notAvailable: "admin.assetTypes.table.access.notAvailable",
	noSubOrganizations: "org-admin.assetTypes.table.access.noSubOrganizations",
	allSubOrganizations: "org-admin.assetTypes.table.access.allSubOrganizations",
	partial: "admin.assetTypes.table.access.partial",
	tooltipNone: "admin.assetTypes.table.access.tooltipNone",
} satisfies ConfigurationTypeTableAccessKeys;

/**
 * Org-admin Asset Types table/export Access column label and badge color.
 */
export function getAssetTypeTableAccessDisplay(
	row: AssetType | AssetTypeDisplay,
	totalSubOrganizations: number,
	t: AppTranslateFn,
): AssetTypeTableAccessDisplay {
	return getConfigurationTypeTableAccessDisplay({
		enabled: row.enabled,
		grantedCount: row.assetTypeSubOrganizations?.length ?? 0,
		totalSubOrganizations,
		keys: ASSET_TYPE_TABLE_ACCESS_KEYS,
		t,
	});
}
